import React from 'react';
import { Box, CircularProgress, Alert, Grid } from '@mui/material';
import { ProductTileCard } from './ProductTileCard';
import { useProductTiles } from '../hooks/useProductTiles';
import { useAppConfig } from '../contexts/ConfigContext';

interface ProductTilesProps {
  onCategoryClick?: (category: string) => void;
}

export const ProductTiles: React.FC<ProductTilesProps> = ({ onCategoryClick }) => {
  const { tiles, loading, error } = useProductTiles();
  const { config } = useAppConfig();

  // Helper function to format the question using the template
  const formatClickQuestion = (category: string): string => {
    const template = config?.product_tiles?.click_question_template || "Show me {category}";
    return template.replace('{category}', category);
  };

  // Get configuration from backend config
  const productTilesConfig = config?.product_tiles;
  
  // Masonry configuration with defaults
  const columnsDesktop = productTilesConfig?.grid_columns_desktop ?? 3;
  const columnsTablet = productTilesConfig?.grid_columns_tablet ?? 2;
  const columnsMobile = productTilesConfig?.grid_columns_mobile ?? 1;
  const gridGap = productTilesConfig?.grid_gap ?? 20;
  
  // Tile sizing
  const aspectRatio = productTilesConfig?.image_aspect_ratio ?? '4:5';
  const minHeight = productTilesConfig?.min_height ?? 300;
  const maxHeight = productTilesConfig?.max_height ?? 600;
  
  // Calculate aspect ratio percentage for Grid items
  const [ratioWidth, ratioHeight] = aspectRatio.split(':').map(Number);
  const aspectRatioPaddingTop = `${(ratioHeight / ratioWidth) * 100}%`;
  
  // Carousel configuration
  const carouselInterval = productTilesConfig?.carousel_interval ?? 3000;
  const autoAdvance = productTilesConfig?.auto_advance ?? true;
  const showDots = productTilesConfig?.show_dots ?? true;
  
  // Mobile configuration
  const swipeEnabled = productTilesConfig?.swipe_enabled ?? true;

  // Show loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 300,
          width: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ width: '100%', p: 2 }}>
        <Alert severity="error">
          Failed to load product tiles: {error}
        </Alert>
      </Box>
    );
  }

  // No tiles available
  if (!tiles || tiles.tiles.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        px: { xs: 2, sm: 3, md: 4 },
        py: 3,
      }}
    >
      <Grid
        container
        spacing={gridGap / 8} // MUI spacing unit is 8px, so 20px = 2.5 units
        sx={{
          margin: 0,
          width: '100%',
          gap: `${gridGap}px`, // Apply explicit gap in pixels
        }}
      >
        {tiles.tiles.map((categoryTile) => (
          <Grid
            item
            key={categoryTile.category}
            xs={12 / columnsMobile}
            sm={12 / columnsTablet}
            md={12 / columnsDesktop}
            sx={{
              position: 'relative',
              '&::before': {
                content: '""',
                display: 'block',
                paddingTop: aspectRatioPaddingTop,
              },
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              }}
            >
              <ProductTileCard
                key={categoryTile.category}
                products={categoryTile.products}
                displayName={categoryTile.display_name}
                category={categoryTile.category}
                autoAdvance={autoAdvance}
                interval={carouselInterval}
                showDots={showDots}
                onCategoryClick={onCategoryClick}
                formatClickQuestion={formatClickQuestion}
                aspectRatio={aspectRatio}
                minHeight={minHeight}
                maxHeight={maxHeight}
                swipeEnabled={swipeEnabled}
              />
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
